import { Injectable, NgZone } from '@angular/core';
import type { ParaScoreButton, StoryScoreButtonProbe } from './lms-header-supplement.util';
import {
	canAttachStoryScoreClickOverlay,
	dismissStoryResumePrompt,
	findStoryPlayerPrevControl,
	findStoryScoreButtonElement,
	findVisibleStoryNextControl,
	hideStoryHiddenAccTextElements,
	isStoryPlayerPrevEnabledForNavigation,
	LMS_STORY_HIDDEN_ACC_TEXTS,
	resolveStoryPlayerPrevBridgeTarget,
} from './lms-story-dom.util';

export interface LmsStoryLayoutSnapshot {
	scoreButtonProbes: StoryScoreButtonProbe[];
	visibleScoreButtons: ParaScoreButton[];
	viewWidth: number;
	nextControlVisible: boolean;
	intersectionRatios: ReadonlyMap<ParaScoreButton, number>;
}

export interface LmsStoryIframeBridgeHandlers {
	isActive: () => boolean;
	onScoreClick: (score: ParaScoreButton) => void;
	onLayoutChange: (snapshot: LmsStoryLayoutSnapshot) => void;
	shouldInterceptPrev: () => boolean;
	onPrevCrossChallenge: () => void;
	canGoToPreviousChallenge: () => boolean;
	debugPrev?: (context: string, extra?: Record<string, unknown>) => void;
}

/** Storyline iframe DOM bridge: observers, score click overlays, PREV bridge, layout snapshots. */
@Injectable({ providedIn: 'root' })
export class LmsStoryIframeBridge {
	private handlers: LmsStoryIframeBridgeHandlers | null = null;
	private observedIframe: HTMLIFrameElement | null = null;
	private iframeLoadHandler: (() => void) | null = null;
	private mutationObserver: MutationObserver | null = null;
	private scoreButtonIntersectionObserver: IntersectionObserver | null = null;
	private scoreButtonIntersectionRatios = new Map<ParaScoreButton, number>();
	private storyHintRefreshTimer: ReturnType<typeof setTimeout> | undefined;
	private storyOverlaySyncTimer: ReturnType<typeof setTimeout> | undefined;
	private storyOverlaySyncRafId: number | null = null;
	private storyIframeResizeObserver: ResizeObserver | null = null;
	private storyIframeWindowResizeHandler: (() => void) | null = null;

	constructor(private readonly ngZone: NgZone) {}

	injectInitialStyles(doc: Document): void {
		try {
			const style = doc.createElement('style');
			style.textContent = `
					body, html, #bg, .story-bg, #playback, #frame {
						background-color: #243670 !important;
					}
					${LMS_STORY_HIDDEN_ACC_TEXTS.map(
						(accText) => `[data-acc-text="${accText}"] {
						display: none !important;
						visibility: hidden !important;
						pointer-events: none !important;
					}`
					).join('\n')}
				`;
			doc.head.appendChild(style);
			hideStoryHiddenAccTextElements(doc);
			dismissStoryResumePrompt(doc);
		} catch (e) {
			console.warn('Could not inject styles into iframe', e);
		}
	}

	attach(iframe: HTMLIFrameElement, handlers: LmsStoryIframeBridgeHandlers): void {
		this.detach();
		this.handlers = handlers;
		this.observedIframe = iframe;

		const config = { childList: true, subtree: true };
		const mutationCallback = () => {
			if (iframe.contentDocument) {
				hideStoryHiddenAccTextElements(iframe.contentDocument);
				dismissStoryResumePrompt(iframe.contentDocument);
			}
			this.ensureScoreButtonIntersectionObserver(iframe);
			this.ensureStoryPlayerPrevBridge(iframe);
			this.scheduleStoryControlHintRefresh(iframe);
		};

		this.mutationObserver = new MutationObserver(mutationCallback);

		const attachToBody = () => {
			this.mutationObserver?.disconnect();
			if (iframe.contentDocument?.body && this.mutationObserver) {
				this.mutationObserver.observe(iframe.contentDocument.body, config);
			}
			this.attachStoryIframeResizeTracking(iframe);
			this.ensureScoreButtonIntersectionObserver(iframe);
			this.ensureStoryPlayerPrevBridge(iframe);
			this.scheduleStoryControlHintRefresh(iframe);
		};

		this.iframeLoadHandler = () => attachToBody();
		iframe.addEventListener('load', this.iframeLoadHandler);
		attachToBody();
	}

	detach(): void {
		if (this.storyHintRefreshTimer != null) {
			clearTimeout(this.storyHintRefreshTimer);
			this.storyHintRefreshTimer = undefined;
		}
		this.detachStoryIframeResizeTracking();
		this.removeStoryScoreClickOverlays();
		if (this.scoreButtonIntersectionObserver) {
			this.scoreButtonIntersectionObserver.disconnect();
			this.scoreButtonIntersectionObserver = null;
		}
		this.scoreButtonIntersectionRatios.clear();
		if (this.observedIframe && this.iframeLoadHandler) {
			this.observedIframe.removeEventListener('load', this.iframeLoadHandler);
		}
		this.observedIframe = null;
		this.iframeLoadHandler = null;
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}
		this.handlers = null;
	}

	handleWindowResize(): void {
		if (this.observedIframe) {
			this.scheduleStoryScoreOverlaySync(this.observedIframe);
		}
	}

	scheduleRefresh(): void {
		if (this.observedIframe) {
			this.scheduleStoryControlHintRefresh(this.observedIframe);
		}
	}

	private scheduleStoryControlHintRefresh(iframe: HTMLIFrameElement): void {
		if (this.storyHintRefreshTimer != null) {
			clearTimeout(this.storyHintRefreshTimer);
		}
		this.storyHintRefreshTimer = setTimeout(() => {
			this.storyHintRefreshTimer = undefined;
			this.refreshStoryControlHints(iframe);
		}, 80);
	}

	private refreshStoryControlHints(iframe: HTMLIFrameElement): void {
		const doc = iframe.contentDocument;
		const handlers = this.handlers;
		if (!handlers) {
			return;
		}

		if (!handlers.isActive()) {
			if (doc) {
				this.removeStoryScoreClickOverlays(doc);
			}
			this.ngZone.run(() => {
				handlers.onLayoutChange({
					scoreButtonProbes: [],
					visibleScoreButtons: [],
					viewWidth: 0,
					nextControlVisible: false,
					intersectionRatios: this.scoreButtonIntersectionRatios,
				});
			});
			return;
		}

		const viewWidth = doc?.documentElement.clientWidth ?? 0;
		if (doc) {
			this.syncStoryScoreClickOverlays(doc);
		}
		const probes = doc ? this.probeVisibleStoryScoreButtons(doc) : [];
		const visibleScoreButtons = probes.map((p) => p.button);
		const nextControlVisible = doc ? findVisibleStoryNextControl(doc) != null : false;
		if (doc) {
			this.primeStoryPlayerPrevCrossChallengeClick(doc);
		}

		this.ngZone.run(() => {
			handlers.onLayoutChange({
				scoreButtonProbes: probes,
				visibleScoreButtons,
				viewWidth,
				nextControlVisible,
				intersectionRatios: this.scoreButtonIntersectionRatios,
			});
		});
	}

	private probeVisibleStoryScoreButtons(doc: Document): StoryScoreButtonProbe[] {
		const probes: StoryScoreButtonProbe[] = [];
		for (const score of [1, 2, 3, 4] as ParaScoreButton[]) {
			const el = findStoryScoreButtonElement(doc, score);
			if (!el) {
				continue;
			}
			const rect = el.getBoundingClientRect();
			probes.push({
				button: score,
				left: rect.left,
				top: rect.top,
				width: rect.width,
				height: rect.height,
				intersectionRatio: this.scoreButtonIntersectionRatios.get(score),
			});
		}
		return probes;
	}

	private syncStoryScoreClickOverlays(doc: Document): void {
		const handlers = this.handlers;
		if (!handlers?.isActive()) {
			this.removeStoryScoreClickOverlays(doc);
			return;
		}

		const activeScores = new Set<ParaScoreButton>();
		for (const score of [1, 2, 3, 4] as ParaScoreButton[]) {
			const source = findStoryScoreButtonElement(doc, score);
			if (!source || !canAttachStoryScoreClickOverlay(source as HTMLElement)) {
				continue;
			}
			activeScores.add(score);
			this.ensureStoryScoreClickOverlay(doc, score, source as HTMLElement);
		}

		for (const overlay of Array.from(doc.querySelectorAll('[data-para-score-click-overlay]'))) {
			const score = Number((overlay as HTMLElement).dataset.paraScore);
			if (!activeScores.has(score as ParaScoreButton)) {
				overlay.remove();
			}
		}
	}

	private ensureStoryScoreClickOverlay(doc: Document, score: ParaScoreButton, source: HTMLElement): void {
		const rect = source.getBoundingClientRect();
		let overlay = doc.querySelector(
			`[data-para-score-click-overlay][data-para-score="${score}"]`
		) as HTMLElement | null;

		if (!overlay) {
			overlay = doc.createElement('div');
			overlay.dataset.paraScoreClickOverlay = 'true';
			overlay.dataset.paraScore = String(score);
			overlay.setAttribute('aria-hidden', 'true');
			overlay.style.cursor = 'pointer';
			overlay.style.background = 'transparent';
			overlay.style.zIndex = '1000';
			overlay.style.pointerEvents = 'auto';
			overlay.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();
				this.handlers?.onScoreClick(score);
			});
			doc.body.appendChild(overlay);
		}

		overlay.style.position = 'fixed';
		overlay.style.left = `${rect.left}px`;
		overlay.style.top = `${rect.top}px`;
		overlay.style.width = `${rect.width}px`;
		overlay.style.height = `${rect.height}px`;
	}

	private removeStoryScoreClickOverlays(doc?: Document | null): void {
		const targetDoc = doc ?? this.observedIframe?.contentDocument ?? null;
		if (!targetDoc) {
			return;
		}
		for (const overlay of Array.from(targetDoc.querySelectorAll('[data-para-score-click-overlay]'))) {
			overlay.remove();
		}
	}

	private ensureScoreButtonIntersectionObserver(iframe: HTMLIFrameElement): void {
		const doc = iframe.contentDocument;
		const win = doc?.defaultView;
		if (!doc || !win) {
			return;
		}

		if (!this.scoreButtonIntersectionObserver) {
			this.scoreButtonIntersectionObserver = new IntersectionObserver(
				(entries) => {
					let changed = false;
					for (const entry of entries) {
						const score = Number((entry.target as HTMLElement).getAttribute('data-acc-text'));
						if (!Number.isFinite(score) || score < 1 || score > 4) {
							continue;
						}
						this.scoreButtonIntersectionRatios.set(score as ParaScoreButton, entry.intersectionRatio);
						changed = true;
					}
					if (changed && this.observedIframe) {
						this.scheduleStoryControlHintRefresh(this.observedIframe);
					}
				},
				{ root: null, threshold: [0, 0.25, 0.5, 0.75, 1] }
			);
		}

		for (const score of [1, 2, 3, 4] as ParaScoreButton[]) {
			const el = findStoryScoreButtonElement(doc, score);
			if (!el || (el as HTMLElement).dataset.paraHintObserved === 'true') {
				continue;
			}
			(el as HTMLElement).dataset.paraHintObserved = 'true';
			this.scoreButtonIntersectionObserver.observe(el);
		}
	}

	private scheduleStoryScoreOverlaySync(iframe: HTMLIFrameElement): void {
		if (this.storyOverlaySyncRafId != null) {
			cancelAnimationFrame(this.storyOverlaySyncRafId);
		}
		this.storyOverlaySyncRafId = requestAnimationFrame(() => {
			this.storyOverlaySyncRafId = requestAnimationFrame(() => {
				this.storyOverlaySyncRafId = null;
				const doc = iframe.contentDocument;
				if (doc) {
					this.syncStoryScoreClickOverlays(doc);
				}
			});
		});

		if (this.storyOverlaySyncTimer != null) {
			clearTimeout(this.storyOverlaySyncTimer);
		}
		this.storyOverlaySyncTimer = setTimeout(() => {
			this.storyOverlaySyncTimer = undefined;
			this.scheduleStoryControlHintRefresh(iframe);
		}, 150);
	}

	private attachStoryIframeResizeTracking(iframe: HTMLIFrameElement): void {
		this.detachStoryIframeResizeTracking();

		if (typeof ResizeObserver !== 'undefined') {
			this.storyIframeResizeObserver = new ResizeObserver(() => {
				this.scheduleStoryScoreOverlaySync(iframe);
			});
			this.storyIframeResizeObserver.observe(iframe);
			const docRoot = iframe.contentDocument?.documentElement;
			if (docRoot) {
				this.storyIframeResizeObserver.observe(docRoot);
			}
		}

		const win = iframe.contentWindow;
		if (win) {
			this.storyIframeWindowResizeHandler = () => this.scheduleStoryScoreOverlaySync(iframe);
			win.addEventListener('resize', this.storyIframeWindowResizeHandler);
		}
	}

	private detachStoryIframeResizeTracking(): void {
		if (this.storyOverlaySyncRafId != null) {
			cancelAnimationFrame(this.storyOverlaySyncRafId);
			this.storyOverlaySyncRafId = null;
		}
		if (this.storyOverlaySyncTimer != null) {
			clearTimeout(this.storyOverlaySyncTimer);
			this.storyOverlaySyncTimer = undefined;
		}
		this.storyIframeResizeObserver?.disconnect();
		this.storyIframeResizeObserver = null;
		if (this.storyIframeWindowResizeHandler) {
			this.observedIframe?.contentWindow?.removeEventListener('resize', this.storyIframeWindowResizeHandler);
		}
		this.storyIframeWindowResizeHandler = null;
	}

	private primeStoryPlayerPrevCrossChallengeClick(doc: Document): void {
		const handlers = this.handlers;
		if (!handlers) {
			return;
		}
		const prevEl = findStoryPlayerPrevControl(doc);
		if (!prevEl) {
			return;
		}
		const prevEnabled = isStoryPlayerPrevEnabledForNavigation(prevEl);
		if (prevEnabled || !handlers.canGoToPreviousChallenge()) {
			return;
		}
		const target = resolveStoryPlayerPrevBridgeTarget(prevEl);
		target.style.pointerEvents = 'auto';
		target.style.cursor = 'pointer';
		handlers.debugPrev?.('primed disabled player PREV for cross-challenge click', {
			prevEnabled,
			bridgeTarget: target.tagName,
		});
	}

	private ensureStoryPlayerPrevBridge(iframe: HTMLIFrameElement): void {
		const handlers = this.handlers;
		const doc = iframe.contentDocument;
		if (!handlers || !doc || !handlers.isActive()) {
			return;
		}
		this.primeStoryPlayerPrevCrossChallengeClick(doc);
		const prevEl = findStoryPlayerPrevControl(doc);
		if (!prevEl) {
			const prevCandidates = Array.from(
				doc.querySelectorAll('[data-acc-text="PREV"], [data-acc-text="Prev"], [data-acc-text="prev"], [aria-label*="PREV" i], [aria-label*="Previous" i]')
			).length;
			handlers.debugPrev?.('player PREV not found in iframe — bridge not attached', { prevCandidates });
			return;
		}
		const target = resolveStoryPlayerPrevBridgeTarget(prevEl);
		if (target.dataset.paraPrevBridged === 'true') {
			return;
		}
		target.dataset.paraPrevBridged = 'true';
		handlers.debugPrev?.('attached PREV cross-challenge bridge', {
			prevEnabled: isStoryPlayerPrevEnabledForNavigation(prevEl),
			bridgeTarget: target.tagName,
		});
		const handler = (event: Event) => {
			const prevEnabled = isStoryPlayerPrevEnabledForNavigation(prevEl);
			const shouldIntercept = handlers.shouldInterceptPrev();
			handlers.debugPrev?.(`player PREV ${event.type}`, { prevEnabled, shouldIntercept });
			if (!shouldIntercept) {
				return;
			}
			handlers.debugPrev?.('INTERCEPT — loading previous catalog challenge');
			event.preventDefault();
			event.stopImmediatePropagation();
			this.ngZone.run(() => handlers.onPrevCrossChallenge());
		};
		target.addEventListener('pointerdown', handler, true);
		target.addEventListener('click', handler, true);
	}
}
